import { TYPE } from '@kontentino/kontentino-constants/Pages';
import SelectNewProfileType from 'components/modals/newSocialMediaProfile/SelectNewProfileType';
import Modal from 'components/shared/Modal';
import React, { FC, useEffect, useState } from 'react';
import { ModalRouteProps, useModalHistory } from 'context/ModalRouter';
import { ActivityIndicator } from '@kontentino/ui';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import { useTranslation } from 'react-i18next';
import { modalRoutes } from 'constants/routes';
import { addQueryParams } from 'utils/url';
import SelectFacebookTokenType from 'components/modals/newSocialMediaProfile/SelectFacebookTokenType';
import useMetaTokenTypeStep from 'components/modals/newSocialMediaProfile/hooks/useMetaTokenTypeStep';
import AutomaticForm from 'components/modals/newSocialMediaProfile/automaticForm/Form';
import useAddNewProfileAutomatically from 'app/modules/posts/hooks/useAddNewProfileAutomatically';
import { SocialLoginTokenType } from 'types/SocialLogin';

export const AnalyticsAddNewProfileModal: FC<ModalRouteProps> = ({
  modalManager,
}) => {
  const { t } = useTranslation();
  const socialLogin = useSocialLoginContext();
  const modalHistory = useModalHistory();
  const [isSelectingPages, setIsSelectingPages] = useState(false);
  const automaticPageAdded = useAddNewProfileAutomatically({});

  const proceedAuthorize = (
    pageType: number,
    tokenType?: SocialLoginTokenType,
  ) => {
    socialLogin.authorize({
      pageType,
      tokenType,
      redirectUrl: `${addQueryParams(
        window.location.href.replace(window.location.hash, ''),
        {
          type: pageType,
        },
      )}#${modalRoutes.ASSOCIATE_POSTS_PROFILE}`,
    });
  };

  const metaTokenTypeStep = useMetaTokenTypeStep(proceedAuthorize, null);

  const proceedAuthorizeObo = () => {
    proceedAuthorize(
      metaTokenTypeStep.selectedPageType ?? TYPE.FACEBOOK,
      'system_user_obo',
    );
  };

  function backToTokenEntry() {
    setIsSelectingPages(false);
    socialLogin.reset();
    modalHistory.replace({ search: '' });
  }

  const isChoosingMethod =
    !isSelectingPages &&
    !socialLogin.isLoading &&
    metaTokenTypeStep.isChoosingTokenType;

  useEffect(() => {
    return () => {
      socialLogin.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      modalManager={modalManager}
      size="responsive"
      dataName={{
        wrapper: 'analytics_add-profile-modal',
        closeButton: 'analytics_add-profile-modal_close-icon',
      }}
      dataCy={{
        wrapper: 'analytics_add-profile-modal',
      }}
      classNames={{ modal: '!tw-max-w-[440px]' }}
    >
      <Modal.Header
        title={
          isSelectingPages
            ? t('associateProfileTitle')
            : isChoosingMethod
            ? t('newSocialMediaProfile.methodCards.title')
            : t('analytics.addProfileModal.title')
        }
        onGoBack={
          isSelectingPages
            ? backToTokenEntry
            : metaTokenTypeStep.isChoosingTokenType
            ? metaTokenTypeStep.reset
            : undefined
        }
      />
      {isSelectingPages && (
        <AutomaticForm
          {...socialLogin}
          pageType={TYPE.FACEBOOK}
          onPageAdded={automaticPageAdded.onPageAdded}
          onGoBack={backToTokenEntry}
        />
      )}
      {!isSelectingPages && (
        <Modal.Content className="tw-mb-6">
          {isChoosingMethod && (
            <SelectFacebookTokenType
              onSelectPersonal={metaTokenTypeStep.handleSelectPersonal}
              onSelectObo={proceedAuthorizeObo}
            />
          )}
          {!socialLogin.isLoading && metaTokenTypeStep.view === 'network' && (
            <SelectNewProfileType
              socialMediaTypes={[
                TYPE.FACEBOOK,
                TYPE.INSTAGRAM,
                TYPE.LINKEDIN,
                TYPE.TIKTOK,
              ]}
              onSelectSocialMediaType={metaTokenTypeStep.handleSelectNetwork}
              dataNamePrefix={{
                socialTypeOption: 'analytics_add-profile-modal_',
              }}
            />
          )}
          {socialLogin.isLoading && (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6">
              <ActivityIndicator className="tw-h-12 tw-w-12" />
              <p className="tw-mt-6 tw-text-md">
                {t('analytics.addProfileModal.authorizing')}
              </p>
            </div>
          )}
        </Modal.Content>
      )}
    </Modal>
  );
};
